import React, { useEffect } from "react";
import { dropDown } from "../../asset";
import AttendanceHeader from "./AttendanceHeader";
import DownloadButton from "../../components/common/DownloadButton";
import { useParams, useSearchParams } from "react-router-dom";
import { StaffMonthlyAttendance } from "../../types/attendanceType";
import attendanceApis from "../../api/attendanceApis";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { fetchStaff } from "../../features/staff/staffSlice";
import {
  getStatusStyles,
  MONTHS,
  STATUS_DISPLAY_MAP,
} from "../../util/Constants";
import { exportToExcel } from "../../util/functions/UniversalFunctions";

export default function StaffAttendanceData() {
  const [searchParams] = useSearchParams();

  const [attendanceMap, setAttendanceMap] = React.useState<
    Record<string, StaffMonthlyAttendance[]>
  >({});
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const { staffList, loading: staffLoading } = useSelector(
    (state: RootState) => state.staff,
  );

  const { branchCode } = useParams<{ branchCode: string }>();

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [dispatch, branchCode]);

  const yearParam = Number(searchParams.get("year"));
  const monthParam = searchParams.get("month");

  const initialMonthIndex = MONTHS.indexOf(monthParam ?? "JANUARY");

  const [current, setCurrent] = React.useState(
    new Date(yearParam, initialMonthIndex, 1),
  );

  const year = current.getFullYear();
  const monthIndex = current.getMonth();
  const monthName = MONTHS[monthIndex];

  const monthLabel = current.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const prevMonth = () =>
    setCurrent((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));

  const nextMonth = () =>
    setCurrent((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const dateObj = new Date(year, monthIndex, i + 1);

    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");

    return {
      date: dateObj.toLocaleString("default", {
        month: "short",
        day: "2-digit",
      }),
      day: dateObj.toLocaleString("default", { weekday: "short" }),
      fullDate: `${yyyy}-${mm}-${dd}`,
    };
  });

  const attendanceByEmployee: Record<string, Record<string, string>> = {};

  Object.entries(attendanceMap).forEach(([date, records]) => {
    records.forEach((r) => {
      const empKey = String(r.staffId);
      if (!attendanceByEmployee[empKey]) {
        attendanceByEmployee[empKey] = {};
      }
      attendanceByEmployee[empKey][date] = r.status ?? "";
    });
  });

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      const res = await attendanceApis.getStaffMonthlyAttendance(
        branchCode,
        String(year),
        monthName,
      );

      if (res?.data) {
        setAttendanceMap(res.data);
      }
      setLoading(false);
    };

    fetchAttendance();
  }, [current, branchCode]);

  const buildExportData = () => {
    return staffList.map((emp) => {
      const row: Record<string, string> = {
        "Employee Code": emp.employeeCode,
        "Employee Name": emp.fullName,
      };

      const empAttendance = attendanceByEmployee[emp.staffId] || {};

      days.forEach((day) => {
        const status = empAttendance[day.fullDate];

        row[day.fullDate] = STATUS_DISPLAY_MAP[status];
      });

      return row;
    });
  };

  return (
    <div className="px-5 py-6 min-h-screen flex flex-col gap-[14px]">
      <AttendanceHeader />

      <div className="flex flex-col gap-4">
        <div className="relative flex items-center">
          <h3 className="text-[28px] text-neutral-400 font-semibold">
            MONTHLY ATTENDANCE REPORT
          </h3>

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
            <button onClick={prevMonth}>
              <img src={dropDown} alt="" className="rotate-90 w-4 h-4" />
            </button>

            <h2 className="text-2xl font-medium text-neutral-500 text-center min-w-[140px]">
              {monthLabel}
            </h2>

            <button onClick={nextMonth}>
              <img src={dropDown} alt="" className="-rotate-90 w-4 h-4" />
            </button>
          </div>

          <div className="ml-auto">
            <DownloadButton
              onClick={() => {
                const data = buildExportData();
                exportToExcel(data, `Staff_Attendance_${monthName}_${year}`);
              }}
            />
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar rounded-lg border border-neutral-50 shadow-sm bg-white">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-background-200 border-b border-neutral-50">
                <th className="p-4 text-sm font-semibold text-neutral-400 min-w-[250px] sticky left-0 bg-background-200">
                  Employees
                </th>
                {days.map((d, i) => (
                  <th
                    key={i}
                    className="p-2 text-center border-l border-neutral-50 min-w-[70px]"
                  >
                    <div className="text-sm font-medium text-neutral-500">
                      {d.date}
                    </div>
                    <div className="text-[13px] text-neutral-300 font-medium">
                      {d.day}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staffList?.map((emp, idx) => (
                <tr
                  key={idx}
                  className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors"
                >
                  <td className="p-3 flex items-center gap-3 sticky left-0 bg-white z-50 border-r">
                    {/* <img
                      src={emp.employeeCode}
                      alt=""
                      className="w-8 h-8 rounded-full border shadow-sm"
                    /> */}
                    <div>
                      {!emp?.profile_image_url ? (
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
                          {emp.fullName.charAt(0).toUpperCase()}
                        </div>
                      ) : (
                        <img
                          src={emp?.profile_image_url}
                          alt="profile"
                          className="w-10 h-10 rounded-full object-cover bg-background-200"
                        />
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-neutral-400 tracking-tighter uppercase">
                        {emp.employeeCode}
                      </span>
                      <span className="text-[13px] font-semibold text-neutral-400 truncate w-32">
                        {emp.fullName}
                      </span>
                    </div>
                  </td>
                  {days.map((day, i) => {
                    const empAttendance =
                      attendanceByEmployee[emp.staffId] || {};
                    const status = empAttendance[day.fullDate] || "";

                    const cellDate = new Date(day.fullDate);
                    cellDate.setHours(0, 0, 0, 0);

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const displayStatus = STATUS_DISPLAY_MAP[status]
                      ? STATUS_DISPLAY_MAP[status]
                      : cellDate < today
                        ? "-"
                        : "";

                    const underlineClass =
                      "bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-[length:50%_3px] bg-[position:50%_80%]";

                    return (
                      <td
                        key={i}
                        className={`p-2 text-center text-sm font-semibold border-l border-neutral-50
        ${displayStatus && displayStatus !== "-" ? underlineClass : ""}
        ${getStatusStyles(status)}
      `}
                      >
                        {displayStatus}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
